import { useState, useEffect } from "react";
import { Menu, X, MessageCircle, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { BRAND, NAV_LINKS, buildWhatsAppLink } from "@/data/lakaData";
import LakaLogo from "./LakaLogo";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const wa = buildWhatsAppLink(
    "Hola LAKA CLUB BEAUTY, quiero pedir cita. ¿Me indicáis disponibilidad?",
  );

  return (
    <header
      data-testid="header-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FBF6F2]/95 backdrop-blur-md border-b border-[#D6B36A]/25"
          : "bg-[#FBF6F2]/70 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => goTo("inicio")}
          className="flex items-center gap-3 group"
        >
          <LakaLogo className="h-11 md:h-12" variant="dark" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => goTo(l.id)}
              className="text-[11px] font-medium tracking-[0.22em] uppercase text-[#1A1A1A] hover:text-[#D6B36A] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="header-whatsapp-link"
            href={wa}
            target="_blank"
            rel="noreferrer"
            className="w-10 h-10 flex items-center justify-center border border-[#1A1A1A]/20 hover:border-[#D6B36A] hover:text-[#D6B36A] transition-colors rounded-sm"
            aria-label="WhatsApp"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <button
            data-testid="header-book-btn"
            onClick={() => goTo("contacto")}
            className="laka-btn-primary text-[0.7rem] py-3 px-6"
          >
            Pedir cita
          </button>
        </div>

        {/* Mobile */}
        <div className="flex lg:hidden items-center gap-2">
          <a
            data-testid="header-mobile-whatsapp"
            href={wa}
            target="_blank"
            rel="noreferrer"
            className="w-9 h-9 flex items-center justify-center border border-[#1A1A1A]/20 rounded-sm"
            aria-label="WhatsApp"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="header-mobile-menu-btn"
                className="w-9 h-9 flex items-center justify-center border border-[#1A1A1A]/20 rounded-sm"
                aria-label="Menú"
              >
                <Menu className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#FBF6F2] border-l border-[#D6B36A]/25 w-[85vw] sm:w-[380px] p-8"
            >
              <SheetTitle className="sr-only">Menú de navegación LAKA CLUB BEAUTY</SheetTitle>
              <SheetDescription className="sr-only">
                Navega entre las secciones de la web y contacta con nosotros.
              </SheetDescription>
              <div className="flex items-center justify-between mb-10">
                <LakaLogo className="h-10" variant="dark" />
                <button
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 flex items-center justify-center"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="laka-hairline mb-8" />
              <nav className="flex flex-col gap-6">
                {NAV_LINKS.map((l, i) => (
                  <button
                    key={l.id}
                    data-testid={`mobile-nav-${l.id}-link`}
                    onClick={() => goTo(l.id)}
                    className="text-left font-serif-laka text-3xl text-[#1A1A1A] hover:text-[#D6B36A] transition-colors"
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    {l.label}
                  </button>
                ))}
              </nav>
              <div className="laka-hairline my-8" />
              <button
                data-testid="mobile-book-btn"
                onClick={() => goTo("contacto")}
                className="laka-btn-primary w-full"
              >
                Pedir cita
              </button>
              <a
                data-testid="mobile-call-link"
                href={BRAND.phoneHref}
                className="mt-4 flex items-center justify-center gap-2 text-xs tracking-[0.22em] uppercase text-[#1A1A1A]/70"
              >
                <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
                {BRAND.phone}
              </a>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
